import { RefObject } from 'react';
import { Message } from '@twilio/conversations';

import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';

import { ConversationReducerActionType } from 'hooks/conversation/types';
import { FORCED_SYSTEM_TYPING_DURATION } from 'components/TypingIndicator/constants';
import { ACTIONS } from 'constants/hook';
import { CHATUI_WINDOW_PATH } from 'constants/global';

/**
 * There is a rare occurrence where a new message from Sebastian may not display in UI but exists in the
 * Twilio conversation instance moments later. Perform check and re-sync at defined time intervals to not hinder
 * customer UX. Even with this logic, this will often result in no work done. Exists as a failsafe.
 *
 * TODO - determine if 100 messages (ChatUI limit) impacts logic.
 */

interface IResyncUtilProps {
  newMessage: Message;
  dispatchConversationAction: (action: ConversationReducerActionType) => void;
  hasLatestMsgAlreadyResynced: { current: RefObject<boolean> | boolean };
  resyncLastMsgIndex: { current: RefObject<number> | number };
}

let intervalInstance: NodeJS.Timeout;

export const resyncMessagesIfNeeded = async ({
  newMessage,
  dispatchConversationAction,
  hasLatestMsgAlreadyResynced,
  resyncLastMsgIndex,
}: IResyncUtilProps) => {
  const pingIntervals = [FORCED_SYSTEM_TYPING_DURATION / 2, FORCED_SYSTEM_TYPING_DURATION];

  intervalInstance && clearInterval(intervalInstance);
  pingIntervals.forEach((pingMs) => {
    // namespace is shared for same message's ping intervals but will be replaced when user sends another message.
    intervalInstance = setTimeout(async () => {
      const messagesDelay = await window[CHATUI_WINDOW_PATH].getMessages();
      if (!hasLatestMsgAlreadyResynced.current) {
        const hookStateLastMsgIndex = newMessage.index;
        const twilioInstanceLastMsgIndex = messagesDelay.length - 1;
        // FIXME - block below is more restrictive. Experiment to see if it will log some data. Use instead if working.
        if (resyncLastMsgIndex.current !== twilioInstanceLastMsgIndex) {
          DataDogLogger.action(CUSTOM_ACTION_NAMES.RESYNC_MESSAGES_LIST, {
            pingMs,
            hookStateLastMsgIndex,
            hookStateLastMsg: messagesDelay[hookStateLastMsgIndex]?.text,
            resyncLastMsgIndex: resyncLastMsgIndex.current,
            twilioInstanceLastMsgIndex,
          });
        }

        // FIXME - block below will always execute at least once for each new user message. Some inefficiency
        //         so ideally we use block the above instead. But this is the most reliable for re-syncing.
        if (hookStateLastMsgIndex !== twilioInstanceLastMsgIndex) {
          dispatchConversationAction({
            type: ACTIONS.CHANGE,
            payload: {
              messages: messagesDelay,
              isAnimateLastMessage: true,
              unixTimeLastMsgReceived: Date.now(),
            },
          });
          hasLatestMsgAlreadyResynced.current = true;
        }
      }
    }, pingMs);
  });
};
